import { useCallback, useEffect, useRef, useState } from "react";

import type { OrgBillingSummary } from "@domains/application/types/billing";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { useSse, type SseMessage } from "@platform/hooks/useSse";

export interface OrgStackStatus {
  alias: string;
  name: string;
  status: string;
}

export interface UseOrgRealtimeResult {
  stacks: OrgStackStatus[];
  billing: OrgBillingSummary | null;
  billingLoading: boolean;
  connected: boolean;
  refreshStacks: () => void;
  refreshBilling: () => void;
}

/**
 * useOrgRealtime — fetches org-level stack statuses and billing summary.
 *
 * Uses SSE (sse/org/:orgAlias) for real-time updates. On mount, a one-shot
 * REST fetch seeds the initial data. SSE events then drive incremental
 * refreshes:
 *   - org.stack_status_changed → refetch stack list
 *   - org.billing_updated → refetch billing summary
 *   - vcs_connection.status_changed → no-op (handled by useVcsConnections)
 *
 * EventSource auto-reconnects natively; on disconnect, the one-shot REST
 * seed re-primes the data when the connection is restored.
 */
export function useOrgRealtime(
  orgAlias: string | undefined,
): UseOrgRealtimeResult {
  const [stacks, setStacks] = useState<OrgStackStatus[]>([]);
  const [billing, setBilling] = useState<OrgBillingSummary | null>(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [connected, setConnected] = useState(false);

  const fetchStacks = useCallback(async () => {
    if (!orgAlias) return;
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}/api/v1/applications?limit=100&offset=0`,
        {
          credentials: "include",
        },
      );
      if (!res.ok) return;
      const data = (await res.json()) as {
        applications: { alias: string; name: string; status: string }[];
      };
      setStacks(
        data.applications.map((s) => ({
          alias: s.alias,
          name: s.name,
          status: s.status,
        })),
      );
    } catch {
      // silent — initial fetch failure
    }
  }, [orgAlias]);

  const fetchBilling = useCallback(async () => {
    if (!orgAlias) return;
    setBillingLoading(true);
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}/api/v1/billing/${encodeURIComponent(orgAlias)}/summary`,
        {
          credentials: "include",
        },
      );
      if (!res.ok) return;
      const data = await res.json();
      setBilling(data as OrgBillingSummary);
    } catch {
      // silent — initial fetch failure
    } finally {
      setBillingLoading(false);
    }
  }, [orgAlias]);

  // Initial data fetch
  useEffect(() => {
    if (!orgAlias) return;
    void fetchStacks();
    void fetchBilling();
  }, [orgAlias, fetchStacks, fetchBilling]);

  // SSE subscription for real-time updates
  const handleMessage = useCallback(
    (msg: SseMessage) => {
      if (msg.type === "connected") {
        setConnected(true);
        return;
      }
      if (msg.type === "org.stack_status_changed") {
        void fetchStacks();
        return;
      }
      if (msg.type === "org.billing_updated") {
        void fetchBilling();
        return;
      }
    },
    [fetchStacks, fetchBilling],
  );

  const sse = useSse(orgAlias ? `sse/org/${orgAlias}` : null, handleMessage);

  // Track previous connected state to detect actual disconnect transitions
  const prevConnectedRef = useRef(false);
  useEffect(() => {
    const wasConnected = prevConnectedRef.current;
    prevConnectedRef.current = sse.connected;
    setConnected(sse.connected);
    // Only refetch on actual disconnect (was connected, now disconnected).
    // On initial mount sse.connected is false and wasConnected is false,
    // so this won't trigger a redundant fetch alongside the initial seed.
    if (!sse.connected && wasConnected && orgAlias) {
      void fetchStacks();
      void fetchBilling();
    }
  }, [sse.connected, orgAlias, fetchStacks, fetchBilling]);

  return {
    stacks,
    billing,
    billingLoading,
    connected,
    refreshStacks: fetchStacks,
    refreshBilling: fetchBilling,
  };
}
